import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt, FormTabType, ImagePrompt, TaskSettings } from '@/app/types';
import { toast } from '../components/ui/use-toast';
import { MODEL_VERSION_SETTINGS_MAP } from '../constants';
import { getAvailableSettingsConfig, stringifyTextPromptWithReference } from '../utils';
import { useFormValidation } from './use-form-validation';
import { useGlobalModal } from './use-global-modal';
import { useTrials } from './use-trials';

const MIN_PROMPT_TOTAL_LENGTH = 10;

export const useForm = (type: Exclude<FormTabType, 'scene'>, onOpenPlanDialog?: (fromPage?: string) => void) => {
  const t = useTranslations();
  const { updateTrialsAsync } = useTrials();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const store = useStoreApi();
  const { modelVersion, formParams, remainingTrials, updateFormParams, getValidSettings, trialsProMode, isFreeUser } =
    useStore((state) => ({
      modelVersion: state[`${type}FormParams`].modelVersion,
      formParams: state[`${type}FormParams`],
      remainingTrials: state.trials720p?.remaining,
      updateFormParams: state.updateFormParams,
      getValidSettings: state.getValidSettings,
      trialsProMode: state.trialsProMode,
      isFreeUser: state.userInfo?.subs_plan === 'free',
    }));

  const {
    errorMessage: formValidationErrorMessage,
    shouldUpgradeForModel,
    handleUpgradeClick,
  } = useFormValidation({
    taskType: type,
    formParams,
    onOpenPlanDialog,
  });

  const settingsKey = MODEL_VERSION_SETTINGS_MAP[modelVersion];
  const settings = formParams[settingsKey];
  const settingsConfig = getAvailableSettingsConfig(modelVersion, type, {
    resolution: String(settings.resolution ?? ''),
  });

  const updateTextPrompt = (textPrompt: string) => {
    updateFormParams(type, 'textPrompt', textPrompt);
  };

  const updateImagePrompt = (idx: number, imagePrompt: FormImagePrompt | null) => {
    // 局部更新
    const draft = [...store.getState()[`${type}FormParams`].imagePrompts];
    draft[idx] = imagePrompt;
    updateFormParams(type, 'imagePrompts', draft);
  };

  // 根据id更新图片
  const updateImagePromptByIds = (imagePromptMap: { id: string; prompt: ImagePrompt }[]) => {
    const formImagePrompts = [...store.getState()[`${type}FormParams`].imagePrompts];

    imagePromptMap.forEach(({ id, prompt }) => {
      const index = formImagePrompts.findIndex((item) => item?.id === id);
      if (index !== -1) {
        formImagePrompts[index] = {
          ...(formImagePrompts[index] as FormImagePrompt),
          prompt,
        };
      }
    });

    updateFormParams(type, 'imagePrompts', formImagePrompts);
  };

  const updateSettings = (newSettings: Partial<TaskSettings>) => {
    updateFormParams(type, settingsKey, newSettings);
  };

  // 用户是否有专业模式权限
  const canUseProMode = !isFreeUser || !!trialsProMode?.remaining;

  const handleBeforeProModeSubmit = () => {
    if (!canUseProMode) {
      openConfirmDialog({
        content: t('create.proMode.unlockCreate.content'),
        primaryButtonText: t('create.proMode.unlockCreate.upgradeButton'),
        secondaryButtonText: t('create.proMode.unlockCreate.basicButton'),
        onPrimaryAction: onOpenPlanDialog,
        onSecondaryAction: () => {
          // 关闭 isProMode
          updateFormParams(type, 'isProMode', false);
        },
        closable: false,
      });
      return false;
    }

    const { recaptionPrompt } = formParams;
    // 如果Prompt为空或主体+环境描述的字数过短<10个字符，出现弹窗提示
    if (recaptionPrompt) {
      const totalLength =
        stringifyTextPromptWithReference(recaptionPrompt.subject, true)?.length +
        stringifyTextPromptWithReference(recaptionPrompt.environment, true)?.length;

      if (totalLength < MIN_PROMPT_TOTAL_LENGTH) {
        return new Promise<boolean>((resolve) => {
          openConfirmDialog({
            title: t('create.proMode.refineDialog.title'),
            content: t('create.proMode.refineDialog.content'),
            primaryButtonText: t('create.proMode.refineDialog.refine'),
            secondaryButtonText: t('create.proMode.refineDialog.generate'),
            onPrimaryAction: () => {
              // 阻断提交
              resolve(false);
            },
            onSecondaryAction: () => {
              // 继续提交的流程
              resolve(true);
            },
            // 点击蒙层关闭也应该阻断提交
            onClose: () => {
              // 阻断提交
              resolve(false);
            },
            closable: false,
          });
        });
      }
    }
    return true;
  };

  // 检查当前设置对于当前用户是否可用
  const handleCheckSettings = () => {
    const { settings: validSettings, hasInvalidSetting } = getValidSettings(type, settings);

    if (hasInvalidSetting) {
      toast({
        type: 'info',
        message: t('popup.toast.invalidSetting'),
      });

      updateSettings(validSettings);
    }
  };

  useEffect(() => {
    if (remainingTrials === undefined) {
      updateTrialsAsync().then(() => {
        handleCheckSettings();
      });
    } else {
      handleCheckSettings();
    }
  }, [settings]);

  return {
    settings,
    settingsConfig,
    handleCheckSettings,
    formValidationErrorMessage,
    shouldUpgradeForModel,
    handleUpgradeClick,
    updateTextPrompt,
    updateImagePrompt,
    updateImagePromptByIds,
    updateSettings,
    handleBeforeProModeSubmit,
    canUseProMode,
  };
};
